import {
  BULB_FOOTPRINT_H,
  BULB_FOOTPRINT_W,
  DAY_SECONDS,
  GENERATOR_FOOTPRINT,
  GRID_EXPANSION_INTERVAL_AFTER,
  GRID_EXPANSION_SCHEDULE,
  RELAY_FOOTPRINT,
} from '../config/balance.js';
import type { TerrainSnapshot } from '../config/maps.js';
import { expandTerrain, generateMapTerrain } from '../config/maps.js';
import type { CellCoord, CellKind, GameNode, MapId, NodeKind } from './types.js';

export function cellKey(c: CellCoord): string {
  return `${c.x},${c.y}`;
}

export function parseCellKey(key: string): CellCoord {
  const [xs, ys] = key.split(',');
  return { x: Number(xs), y: Number(ys) };
}

/** Orthogonal adjacency (4-dir). */
export function cellsAdjacentOrtho(a: CellCoord, b: CellCoord): boolean {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
}

/** 8-directional adjacency (orthogonal + diagonal). */
export function cellsAdjacent(a: CellCoord, b: CellCoord): boolean {
  const dx = Math.abs(a.x - b.x);
  const dy = Math.abs(a.y - b.y);
  return dx <= 1 && dy <= 1 && !(dx === 0 && dy === 0);
}

/**
 * 8-connected Bresenham line from `from` to `to` (inclusive).
 * Produces natural diagonal runs when both axes advance together.
 */
export function bresenhamLine(from: CellCoord, to: CellCoord): CellCoord[] {
  let x0 = from.x;
  let y0 = from.y;
  const x1 = to.x;
  const y1 = to.y;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  const out: CellCoord[] = [{ x: x0, y: y0 }];
  while (x0 !== x1 || y0 !== y1) {
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
    out.push({ x: x0, y: y0 });
    if (out.length > 512) break;
  }
  return out;
}

export function footprintDims(kind: NodeKind): { w: number; h: number } {
  if (kind === 'bulb') return { w: BULB_FOOTPRINT_W, h: BULB_FOOTPRINT_H };
  if (kind === 'generator') return { w: GENERATOR_FOOTPRINT, h: GENERATOR_FOOTPRINT };
  // relay / switch / teleporter: 1×1
  return { w: RELAY_FOOTPRINT, h: RELAY_FOOTPRINT };
}

/** @deprecated Prefer footprintDims. */
export function footprintSizeForKind(kind: NodeKind): number {
  const d = footprintDims(kind);
  return Math.max(d.w, d.h);
}

/**
 * Absolute grid cell for cable attachment.
 * Generators/relays: their single cell. Bulbs: bottom cell of the 1×2.
 */
export function nodePortCell(node: {
  kind: string;
  anchor: CellCoord;
  portOffset?: CellCoord;
}): CellCoord {
  if (
    node.kind === 'relay' ||
    node.kind === 'switch' ||
    node.kind === 'generator' ||
    node.kind === 'teleporter'
  ) {
    return { x: node.anchor.x, y: node.anchor.y };
  }
  const ox = node.portOffset?.x ?? 0;
  const oy = node.portOffset?.y ?? 1; // bottom of 1×2 by default
  return { x: node.anchor.x + ox, y: node.anchor.y + oy };
}

/**
 * The 5 cable-approach cells around a bulb port:
 * below, down-left, down-right, left, right.
 * At least one must stay free of buildings/obstacles.
 */
export function bulbCableAccessCells(node: {
  kind: string;
  anchor: CellCoord;
  portOffset?: CellCoord;
}): CellCoord[] {
  if (node.kind !== 'bulb') return [];
  const port = nodePortCell(node);
  return [
    { x: port.x, y: port.y + 1 }, // below
    { x: port.x - 1, y: port.y + 1 }, // down-left
    { x: port.x + 1, y: port.y + 1 }, // down-right
    { x: port.x - 1, y: port.y }, // left
    { x: port.x + 1, y: port.y }, // right
  ];
}

/** Primary approach cell (directly under the port). */
export function bulbCableAccessCell(node: {
  kind: string;
  anchor: CellCoord;
  portOffset?: CellCoord;
}): CellCoord | null {
  return bulbCableAccessCells(node)[0] ?? null;
}

export function defaultPortOffset(
  kind: 'generator' | 'bulb' | 'relay' | 'switch' | 'teleporter',
): CellCoord {
  if (kind === 'bulb') return { x: 0, y: 1 }; // bas de l'ampoule
  return { x: 0, y: 0 };
}

export interface GridDims {
  width: number;
  height: number;
}

export function gridDimsForDay(day: number): GridDims {
  let dims: GridDims = {
    width: GRID_EXPANSION_SCHEDULE[0]!.width,
    height: GRID_EXPANSION_SCHEDULE[0]!.height,
  };
  for (const entry of GRID_EXPANSION_SCHEDULE) {
    if (day >= entry.fromDay) {
      dims = { width: entry.width, height: entry.height };
    }
  }
  const last = GRID_EXPANSION_SCHEDULE[GRID_EXPANSION_SCHEDULE.length - 1]!;
  if (day > last.fromDay) {
    const extra = Math.floor((day - last.fromDay) / GRID_EXPANSION_INTERVAL_AFTER);
    dims = {
      width: last.width + extra * 2,
      height: last.height + extra * 2,
    };
  }
  return dims;
}

/** Day (inclusive) when the next +2/+2 expansion occurs after `day`. */
export function nextExpansionDay(day: number): number | null {
  for (const entry of GRID_EXPANSION_SCHEDULE) {
    if (entry.fromDay > day) return entry.fromDay;
  }
  const last = GRID_EXPANSION_SCHEDULE[GRID_EXPANSION_SCHEDULE.length - 1]!;
  if (day < last.fromDay) return last.fromDay;
  // After schedule: every GRID_EXPANSION_INTERVAL_AFTER days
  const steps = Math.floor((day - last.fromDay) / GRID_EXPANSION_INTERVAL_AFTER);
  return last.fromDay + (steps + 1) * GRID_EXPANSION_INTERVAL_AFTER;
}

/** Day when the current grid size bracket started. */
export function currentExpansionStartDay(day: number): number {
  let start = GRID_EXPANSION_SCHEDULE[0]!.fromDay;
  for (const entry of GRID_EXPANSION_SCHEDULE) {
    if (day >= entry.fromDay) start = entry.fromDay;
  }
  const last = GRID_EXPANSION_SCHEDULE[GRID_EXPANSION_SCHEDULE.length - 1]!;
  if (day > last.fromDay) {
    const steps = Math.floor((day - last.fromDay) / GRID_EXPANSION_INTERVAL_AFTER);
    start = last.fromDay + steps * GRID_EXPANSION_INTERVAL_AFTER;
  }
  return start;
}

/**
 * Virtual grid size for camera framing: lerps from current → next expansion
 * so dezoom is continuous and matches the upcoming discrete growth.
 */
export function virtualGridDimsForCamera(
  day: number,
  simTime: number,
  currentWidth: number,
  currentHeight: number,
): GridDims {
  const nextDay = nextExpansionDay(day);
  if (nextDay == null) {
    return { width: currentWidth, height: currentHeight };
  }
  const next = gridDimsForDay(nextDay);
  if (next.width === currentWidth && next.height === currentHeight) {
    return { width: currentWidth, height: currentHeight };
  }
  const startDay = currentExpansionStartDay(day);
  const startT = (startDay - 1) * DAY_SECONDS;
  const endT = (nextDay - 1) * DAY_SECONDS;
  const span = Math.max(0.001, endT - startT);
  let t = (simTime - startT) / span;
  t = Math.max(0, Math.min(1, t));
  // Smoothstep — almost imperceptible early, settles into next size
  const s = t * t * (3 - 2 * t);
  return {
    width: currentWidth + (next.width - currentWidth) * s,
    height: currentHeight + (next.height - currentHeight) * s,
  };
}

/** @deprecated Use gridDimsForDay — returns width for legacy callers. */
export function gridSizeForDay(day: number): number {
  return gridDimsForDay(day).width;
}

export function cellCenterCoord(c: CellCoord): { cx: number; cy: number } {
  return { cx: c.x + 0.5, cy: c.y + 0.5 };
}

export function getTerrainAt(terrain: TerrainSnapshot, c: CellCoord): CellKind {
  if (c.x < 0 || c.y < 0 || c.x >= terrain.width || c.y >= terrain.height) {
    return 'obstacle';
  }
  return terrain.cells[c.y * terrain.width + c.x]!;
}

export function isMagnetic(terrain: TerrainSnapshot, c: CellCoord): boolean {
  return getTerrainAt(terrain, c) === 'magnetic';
}

export function isObstacle(terrain: TerrainSnapshot, c: CellCoord): boolean {
  return getTerrainAt(terrain, c) === 'obstacle';
}

export function footprintCells(anchor: CellCoord, w: number, h: number): CellCoord[] {
  const out: CellCoord[] = [];
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      out.push({ x: anchor.x + dx, y: anchor.y + dy });
    }
  }
  return out;
}

export function nodeOccupiesCell(node: GameNode, c: CellCoord): boolean {
  const { w, h } = footprintDims(node.kind);
  return footprintCells(node.anchor, w, h).some((p) => p.x === c.x && p.y === c.y);
}

export interface OccupancyContext {
  gridWidth: number;
  gridHeight: number;
  terrain: TerrainSnapshot;
  nodes: GameNode[];
  cableCells: Set<string>;
  ignoreNodeId?: string;
}

/** Access cell usable for cables: in-bounds, not obstacle, not occupied by a building. */
export function isBulbAccessCellOpen(ctx: OccupancyContext, c: CellCoord): boolean {
  if (c.x < 0 || c.y < 0 || c.x >= ctx.gridWidth || c.y >= ctx.gridHeight) return false;
  if (isObstacle(ctx.terrain, c)) return false;
  for (const n of ctx.nodes) {
    if (n.id === ctx.ignoreNodeId) continue;
    if (nodeOccupiesCell(n, c)) return false;
  }
  return true;
}

/**
 * True if occupying `c` would leave some bulb with zero free approach cells.
 * Buildings may sit on approach cells as long as ≥1 of the 5 stays open.
 */
function wouldOccupyLastBulbAccess(ctx: OccupancyContext, c: CellCoord): boolean {
  for (const n of ctx.nodes) {
    if (n.kind !== 'bulb' || n.id === ctx.ignoreNodeId) continue;
    const accesses = bulbCableAccessCells(n);
    if (!accesses.some((a) => a.x === c.x && a.y === c.y)) continue;
    const remaining = accesses.filter(
      (a) => !(a.x === c.x && a.y === c.y) && isBulbAccessCellOpen(ctx, a),
    ).length;
    if (remaining === 0) return true;
  }
  return false;
}

export function isCellBlockedForBuilding(ctx: OccupancyContext, c: CellCoord): boolean {
  if (c.x < 0 || c.y < 0 || c.x >= ctx.gridWidth || c.y >= ctx.gridHeight) return true;
  if (isObstacle(ctx.terrain, c)) return true;
  if (ctx.cableCells.has(cellKey(c))) return true;
  if (wouldOccupyLastBulbAccess(ctx, c)) return true;
  for (const n of ctx.nodes) {
    if (n.id === ctx.ignoreNodeId) continue;
    if (nodeOccupiesCell(n, c)) return true;
  }
  return false;
}

export function canPlaceBuilding(
  ctx: OccupancyContext,
  anchor: CellCoord,
  kind: GameNode['kind'],
): boolean {
  const { w, h } = footprintDims(kind);
  if (anchor.x < 0 || anchor.y < 0) return false;
  if (anchor.x + w > ctx.gridWidth || anchor.y + h > ctx.gridHeight) return false;

  const cells = footprintCells(anchor, w, h);
  for (const c of cells) {
    // Footprint must never sit on an obstacle (or other buildings / cables).
    if (c.x < 0 || c.y < 0 || c.x >= ctx.gridWidth || c.y >= ctx.gridHeight) return false;
    if (isObstacle(ctx.terrain, c)) return false;
    if (ctx.cableCells.has(cellKey(c))) return false;
    for (const n of ctx.nodes) {
      if (n.id === ctx.ignoreNodeId) continue;
      if (nodeOccupiesCell(n, c)) return false;
    }
    if (wouldOccupyLastBulbAccess(ctx, c)) return false;
  }

  // Bulbs need ≥1 of the 5 approach cells free (cables OK, buildings/obstacles not).
  if (kind === 'bulb') {
    const probe = {
      kind: 'bulb' as const,
      anchor,
      portOffset: defaultPortOffset('bulb'),
    };
    const open = bulbCableAccessCells(probe).some((c) => isBulbAccessCellOpen(ctx, c));
    if (!open) return false;
  }
  return true;
}

/** Prefer an anchor; fall back to the closest free cell of that kind. */
export function pickBuildingAnchor(
  ctx: OccupancyContext,
  kind: NodeKind,
  preferred: CellCoord,
): CellCoord | null {
  if (canPlaceBuilding(ctx, preferred, kind)) return { ...preferred };
  const free = findFreeAnchorsForKind(ctx, kind);
  if (free.length === 0) return null;
  free.sort((a, b) => {
    const da = Math.abs(a.x - preferred.x) + Math.abs(a.y - preferred.y);
    const db = Math.abs(b.x - preferred.x) + Math.abs(b.y - preferred.y);
    return da - db;
  });
  return { ...free[0]! };
}

/** Ensure building footprints are never painted as obstacles. */
export function clearObstaclesUnderNodes(
  terrain: TerrainSnapshot,
  nodes: GameNode[],
): void {
  for (const n of nodes) {
    const { w, h } = footprintDims(n.kind);
    for (const c of footprintCells(n.anchor, w, h)) {
      if (c.x < 0 || c.y < 0 || c.x >= terrain.width || c.y >= terrain.height) continue;
      const idx = c.y * terrain.width + c.x;
      if (terrain.cells[idx] === 'obstacle') terrain.cells[idx] = 'empty';
    }
  }
}

/** Free anchors for a given kind. */
export function findFreeAnchorsForKind(
  ctx: OccupancyContext,
  kind: NodeKind,
): CellCoord[] {
  const { w, h } = footprintDims(kind);
  const anchors: CellCoord[] = [];
  const maxY = ctx.gridHeight - h;
  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= ctx.gridWidth - w; x++) {
      const anchor = { x, y };
      if (canPlaceBuilding(ctx, anchor, kind)) anchors.push(anchor);
    }
  }
  return anchors;
}

/** @deprecated */
export function findFreeAnchors(ctx: OccupancyContext, size: number): CellCoord[] {
  const kind: NodeKind = size === 1 ? 'generator' : 'bulb';
  return findFreeAnchorsForKind(ctx, kind);
}

/** @deprecated */
export function findFree2x2Anchors(ctx: OccupancyContext): CellCoord[] {
  return findFreeAnchorsForKind(ctx, 'bulb');
}

export interface GridExpandResult {
  newWidth: number;
  newHeight: number;
  terrain: TerrainSnapshot;
  offsetX: number;
  offsetY: number;
}

export function expandGridSymmetric(
  mapId: MapId,
  seed: number,
  currentWidth: number,
  currentHeight: number,
  targetWidth: number,
  targetHeight: number,
  terrain: TerrainSnapshot,
): GridExpandResult {
  if (targetWidth <= currentWidth && targetHeight <= currentHeight) {
    return {
      newWidth: currentWidth,
      newHeight: currentHeight,
      terrain,
      offsetX: 0,
      offsetY: 0,
    };
  }
  const offsetX = Math.floor((targetWidth - currentWidth) / 2);
  const offsetY = Math.floor((targetHeight - currentHeight) / 2);
  const newTerrain = expandTerrain(terrain, targetWidth, targetHeight, mapId, seed);
  return {
    newWidth: targetWidth,
    newHeight: targetHeight,
    terrain: newTerrain,
    offsetX,
    offsetY,
  };
}

export function shiftNodesAfterExpand(
  nodes: GameNode[],
  offsetX: number,
  offsetY: number,
): void {
  for (const n of nodes) {
    n.anchor.x += offsetX;
    n.anchor.y += offsetY;
  }
}

export function shiftCableCellsAfterExpand(
  cells: CellCoord[],
  offsetX: number,
  offsetY: number,
): void {
  for (const c of cells) {
    c.x += offsetX;
    c.y += offsetY;
  }
}

export function createInitialTerrain(
  mapId: MapId,
  width: number,
  height: number,
  seed: number,
): TerrainSnapshot {
  return generateMapTerrain(mapId, width, height, seed);
}
